import { useEffect } from "react";
import { Link, useNavigate } from "react-router-dom";
import { ArrowRight, Sparkles, Users2, Clock, ShieldCheck } from "lucide-react";

export default function Landing() {
  const nav = useNavigate();

  // Route first-time visitors through the intro carousel (every device).
  useEffect(() => {
    const seen = localStorage.getItem("tablo_intro_seen");
    if (!seen) {
      nav("/intro", { replace: true });
    }
  }, [nav]);

  return (
    <div className="min-h-screen bg-cream text-ink">
      <header className="max-w-6xl mx-auto px-6 py-6 flex items-center justify-between">
        <div className="flex items-center gap-2">
          <span className="font-serif text-2xl">Tablo</span>
          <span className="text-[10px] uppercase tracking-[0.18em] text-amber font-medium">Hyderabad</span>
        </div>
        <div className="flex items-center gap-2">
          <Link to="/partner/auth" className="text-sm text-light-ink hover:text-ink" data-testid="landing-partner-link">For Restaurants</Link>
          <Link to="/auth" className="tablo-btn-primary text-sm" data-testid="landing-login-btn">Sign in</Link>
        </div>
      </header>

      <section className="max-w-6xl mx-auto px-6 pt-10 pb-16 grid md:grid-cols-2 gap-10 items-center">
        <div className="space-y-6 animate-fade-up">
          <p className="text-amber tracking-[0.22em] text-xs uppercase font-medium">Group dining & takeaway, simplified</p>
          <h1 className="font-serif text-4xl sm:text-5xl lg:text-6xl tracking-tight leading-[1.05]">
            Book a table for the whole crew — or grab a big takeaway — in <span className="italic">minutes</span>.
          </h1>
          <p className="text-light-ink text-base sm:text-lg max-w-lg">
            Tablo curates Hyderabad's best places for group dine-in and large takeaway orders.
            AI builds the menu around your group's veg/non-veg mix, allergens, and spice tolerance — at a price you set.
          </p>
          <div className="flex flex-wrap gap-3">
            <button
              onClick={() => nav("/auth")}
              className="tablo-btn-primary inline-flex items-center gap-2"
              data-testid="landing-get-started-btn"
            >
              Plan your meal <ArrowRight className="w-4 h-4" strokeWidth={2} />
            </button>
            <Link to="/intro" className="tablo-btn-outline" data-testid="landing-intro-cta">
              How Tablo works
            </Link>
            <Link to="/partner/auth" className="tablo-btn-outline" data-testid="landing-restaurant-cta">
              I run a restaurant
            </Link>
          </div>

          <div className="flex gap-6 pt-6 text-sm text-light-ink">
            <div className="flex items-center gap-2"><Sparkles className="w-4 h-4 text-amber" strokeWidth={1.6}/>AI menu curation</div>
            <div className="flex items-center gap-2"><Users2 className="w-4 h-4 text-amber" strokeWidth={1.6}/>Veg + non-veg split</div>
            <div className="flex items-center gap-2"><ShieldCheck className="w-4 h-4 text-amber" strokeWidth={1.6}/>GST invoices</div>
          </div>
        </div>

        <div className="relative">
          <div className="aspect-[4/5] rounded-3xl overflow-hidden shadow-tablo-lg">
            <img
              src="https://images.pexels.com/photos/19447626/pexels-photo-19447626.jpeg"
              alt="Restaurant interior"
              className="w-full h-full object-cover"
              data-testid="landing-hero-image"
            />
          </div>
          <div className="absolute -bottom-6 -left-6 tablo-card p-4 max-w-[240px] hidden sm:block">
            <div className="flex items-center gap-2 mb-1">
              <Users2 className="w-4 h-4 text-amber" strokeWidth={1.6} />
              <p className="text-xs uppercase tracking-widest text-light-ink">Group of 24</p>
            </div>
            <p className="font-serif text-lg leading-tight">Banjara Hills rooftop, ₹1,800/head locked.</p>
          </div>
        </div>
      </section>

      <section className="max-w-6xl mx-auto px-6 py-10 grid sm:grid-cols-3 gap-5">
        {[
          { k: "Locked prices", v: "No haggling, no surprise bills." },
          { k: "Curated menus", v: "AI builds a set menu for groups of 8+." },
          { k: "HR approvals", v: "One-tap approve, GST invoices auto-sent." },
        ].map((f) => (
          <div key={f.k} className="tablo-card p-6">
            <p className="font-serif text-xl mb-1">{f.k}</p>
            <p className="text-light-ink text-sm">{f.v}</p>
          </div>
        ))}
      </section>

      <footer className="max-w-6xl mx-auto px-6 py-10 text-sm text-light-ink/70 border-t border-tablo-border mt-10">
        © Tablo · Hyderabad pilot · Built for groups of 8–80.
      </footer>
    </div>
  );
}
